import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const AddMultipleProductsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="To add multiple products at once, you can upload a spreadsheet of your products to your Faire shop."
          description={{
            text: "Introduction to adding multiple products",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Here’s how you can do that:"
          description={{
            text: "Header for the steps to add multiple products",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  ProductsLinkFormatting(chunks),
              }}
              description={{
                text: "First step to add multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the <BoldFormatting>Add products</BoldFormatting> dropdown."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to add multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Choose the <BoldFormatting>Upload spreadsheet</BoldFormatting> option."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to add multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Download the blank template and complete the required fields with your new product details, including name, product type, description, image links, and pricing. You can reference the <BoldFormatting>Instructions</BoldFormatting> tab at the beginning of the template for useful tips on what to include."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to add multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Start upload</BoldFormatting>, and choose the completed template to begin your upload."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to add multiple products",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can also import new products directly from Shopify by selecting the <BoldFormatting>Import from Shopify</BoldFormatting> button, logging in to Shopify, and choosing the desired catalog from the dropdown menu."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Alternative method to add multiple products",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
